import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";

import * as FiIcons from "react-icons/fi";

import theme from "../../global/theme";
import { login } from "../../../store/auth/authSlice";
import { AuthWarpper } from "./AuthStyle";

import brandImage from "../../../assets/images/logo.jpeg";
import authBg from "../../../assets/images/authBg.svg";

const Login = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast({ position: "top", duration: 2000, status: "error" });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { role: "admin" } });

  const [isPasswordShown, setIsPasswordShown] = useState(false);

  return (
    <AuthWarpper>
      <Button
        type="button"
        className="lang-btn"
        onClick={() =>
          i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")
        }
      >
        <FiIcons.FiGlobe size={22} />
      </Button>

      <Box
        className="auth-block"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box className="content">
          <Box textAlign="center" mb={6}>
            <img
              src={brandImage}
              alt="Logo"
              style={{ height: 90, margin: "auto", marginBottom: 20 }}
            />
            <Heading color={theme.dark} fontSize={24} mb={1} textTransform={'capitalize'}>
              {t("pages.auth.welcome_1")}
            </Heading>
            <Text fontSize={14} color={theme.text} textTransform={'capitalize'}>
              {t("pages.auth.welcome_2")}
            </Text>
          </Box>

          <form
            onSubmit={handleSubmit((values) => {
              dispatch(login(values))
                .unwrap()
                .then(() => {
                  navigate("/", { replace: true });
                })
                .catch((e) => {
                  toast({ description: e?.message });
                });
            })}
          >
            <Stack spacing={5}>
              <FormControl>
                <FormLabel color={theme.dark} fontSize={14} textTransform={'capitalize'}>
                  {t("pages.auth.email")}
                </FormLabel>
                <Input
                  type="text"
                  placeholder={t("pages.auth.email")}
                  borderRadius={8}
                  border="1px solid"
                  borderColor={theme.border}
                  bg={theme.bg}
                  _focus={{ borderColor: theme.primary }}
                  _placeholder={{ color: theme.text }}
                  {...register("email", {
                    required: `${t("validation.required")}`,
                  })}
                />
                {errors.email && (
                  <Text color={theme.error} fontSize={12} mt={1}>
                    {errors.email.message}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel color={theme.dark} fontSize={14} textTransform={'capitalize'}>
                  {t("pages.auth.password")}
                </FormLabel>
                <InputGroup>
                  <Input
                    type={isPasswordShown ? "text" : "password"}
                    placeholder={t("pages.auth.password")}
                    borderRadius={8}
                    border="1px solid"
                    borderColor={theme.border}
                    bg={theme.bg}
                    _focus={{ borderColor: theme.primary }}
                    _placeholder={{ color: theme.text }}
                    {...register("password", {
                      required: `${t("validation.required")}`,
                      minLength: {
                        value: 6,
                        message: `${t("validation.min_length")} 6`,
                      },
                    })}
                  />
                  <InputRightElement>
                    <Button
                      type="button"
                      onClick={() => setIsPasswordShown(!isPasswordShown)}
                      variant="ghost"
                      size="sm"
                      _hover={{ bg: "transparent" }}
                    >
                      {isPasswordShown ? (
                        <FiIcons.FiEyeOff />
                      ) : (
                        <FiIcons.FiEye />
                      )}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                {errors.password && (
                  <Text color={theme.error} fontSize={12} mt={1}>
                    {errors.password.message}
                  </Text>
                )}
              </FormControl>

              <Button
                type="submit"
                borderRadius={8}
                fontWeight="bold"
                fontSize={15}
                bg={theme.primary}
                color="white"
                py={3}
                _hover={{ bg: theme.primary1 }}
                isLoading={auth.isLoading}
                textTransform={'capitalize'}
              >
                {t("pages.auth.login")}
              </Button>
            </Stack>
          </form>
        </Box>
      </Box>

      <Box className="auth-bg">
        <img src={authBg} alt="DYARKO Auth Background" className="auth-gif" />
      </Box>
    </AuthWarpper>
  );
};

export default Login;
